import { ARCHIVED_BANNER_HEIGHT } from "components/archived-project-banner";
import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useContext,
  useCallback
} from "react";
import { AgGridReact } from "ag-grid-react"; // the AG Grid React Component
import { Button, Modal } from "antd";
import { message } from "utils/ciq-message";
import { ColDef } from "ag-grid-enterprise";
import { InviteProjectUser } from "pages/invite-user/invite-subscription-user";
import {
  dummyColumnDef,
  InviteStatusCellRenderer,
  projectUserRoleRenderer,
  truncatedTextCellRenderer,
  userCompanyRenderer
} from "components/cell-renders";
import { GridLoadingIndicator } from "components/widgets";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import ProjectUserRoleSelectCellEditor from "components/cell-editor/project-user-role-cell-editor";
import { useMutation } from "@apollo/client";
import {
  MUTATION_UPDATE_PROJECT_USER_PERMISSION,
  MUTATION_UPDATE_PROJECT_USER_ACTION
} from "services/graphQL/mutations";
import { getSubscriptionId, getUser } from "services/auth";
import {
  getProjectUserSources,
  isProjectInIntegrationMode,
  matchCustomUserProjectRole
} from "utils/utils";
import ErrorBoundary from "components/error-boundary";
import InviteAccUserComponent from "pages/invite-user/invite-subscription-user/invite-acc-user";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import CiqAgSearch from "components/ciq-ag-search";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import UserCompanySelectCellEditor from "components/cell-editor/user-company-cell-editor";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  QUERY_PROJECT_COMPANY_LIST,
  QUERY_PROJECT_ROLES
} from "services/graphQL/ciq-queries";
import {
  AcceptanceStatus,
  DialogTitles,
  ECustomUserTypes,
  EUserTypes,
  ErrorMessages,
  ProjectPermissionEnum,
  Sources,
  isGcProjectAdminRole
} from "../../constants";
import { useHistory, useParams } from "react-router";

const hasProjectUserCompanyEditAccess = (params: any) => {
  const { context, data } = params;

  const isGcADmin = isGcProjectAdminRole(context?.tokenContents?.role);

  const sources: string[] = getProjectUserSources(data);

  const isCiqSourceUser = sources?.every(
    (source: string) => source === (Sources.ConstructivIQ as string)
  );

  const isPermissionEnabled = isGcADmin && !context?.isProjectArchived;

  const isStatusActiveOrPending =
    params.data.status_id === AcceptanceStatus.ACTIVE ||
    params.data.status_id === AcceptanceStatus.INVITED;

  return isCiqSourceUser && isPermissionEnabled && isStatusActiveOrPending;
};

function ProjectUsersList({
  internalUsers,
  vendorData
}: {
  internalUsers: { project_users: Array<any> };
  vendorData?: any;
}) {
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();
  const gridRef = useRef<AgGridReact>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [drawer, setDrawerVisibility] = useState<boolean>(false);
  const {
    tokenContents,
    gqlClientForProject,
    projectDetails,
    eventLogs,
    isProjectArchived
  } = useContext(ProjectContext);
  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const { data: allProjectRoles } = useCIQQuery(QUERY_PROJECT_ROLES, {
    variables: {
      where: { type_id: { _eq: EUserTypes.GENERAL_CONTRACTOR } }
    },
    skip: !gqlClientForProject,
    client: gqlClientForProject
  });

  const { data: projectCompanies, refetch: refetchProjectCompanies } =
    useCIQQuery(QUERY_PROJECT_COMPANY_LIST, {
      skip: !gqlClientForProject && isIntegrationMode,
      client: gqlClientForProject
    });

  const subscriptionId = getSubscriptionId();
  const isGCADmin =
    isGcProjectAdminRole(tokenContents?.role) && !isProjectArchived;
  const [currentUser]: [any, any] = useState(getUser());
  const [inviteAccUser, setInviteAccUser] = useState<{
    showInviteModel: boolean;
    inviteUserData?: any;
  }>({
    showInviteModel: false,
    inviteUserData: null
  });

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const [updateUserPermission] = useMutation<any>(
    MUTATION_UPDATE_PROJECT_USER_PERMISSION,
    {
      client: gqlClientForProject
    }
  );

  const [updateProjectUserAction] = useMutation<any>(
    MUTATION_UPDATE_PROJECT_USER_ACTION,
    {
      client: gqlClientForProject
    }
  );

  const userAction = async (userId: string, isChecked: boolean) => {
    if (userId) {
      const variables = {
        updates: {
          _set: {
            status_id: isChecked
              ? AcceptanceStatus.ACTIVE
              : AcceptanceStatus.DEACTIVATED
          },
          where: {
            user_id: { _eq: userId }
          }
        }
      };

      try {
        const updateResponse = await updateProjectUserAction({ variables });

        if (
          updateResponse.data?.update_project_users_many &&
          updateResponse.data?.update_project_users_many?.length > 0 &&
          updateResponse.data?.update_project_users_many[0]?.affected_rows === 1
        ) {
          message.success(
            isChecked
              ? "Project user has been activated."
              : "Project user has been deactivated."
          );
        } else {
          message.error("Failed to update.");
        }
      } catch (ex) {
        console.error(ex);
        message.error("An error occured");
      }
    }
  };

  const onCompanyCellEdit = async (event: any) => {
    if (event.newValue?.trim() === event.oldValue?.trim()) return;
    const oldData = { ...event.data };
    Modal.confirm({
      title: (
        <p className="text-ciq-gray-950 text-sm">
          The User&apos;s Company will be changed in this Project ONLY. This
          change will not update the User&apos;s Company at the Subscription
          level or in any other Project. Do you want to proceed?
        </p>
      ),
      okText: "Yes",
      cancelText: "No",
      async onOk() {
        const company = projectCompanies?.project_vendors?.find(
          (c: any) => c?.subscription_vendor?.name === event.newValue
        );
        event.node.setData({
          ...oldData,
          subscription_vendor: {
            name: event?.newValue
          }
        });
        const variables = {
          updates: {
            _set: {
              vendor_id: company.vendor_id
            },
            where: {
              user_id: { _eq: event?.data?.user_id }
            }
          }
        };

        try {
          const updateResponse = await updateProjectUserAction({ variables });
          if (
            updateResponse.data?.update_project_users_many &&
            updateResponse.data?.update_project_users_many?.length > 0 &&
            updateResponse.data?.update_project_users_many[0]?.affected_rows ===
              1
          ) {
            message.success("Project company updated successfully");
          } else {
            message.error("Failed to update.");
          }
        } catch (ex) {
          console.error(ex);
          message.error("An error occured");
        }
      },
      onCancel: () => {
        event.node.setData({
          ...oldData
        });
      }
    });
  };

  const gridContext = {
    allProjectRoles: allProjectRoles?.project_role,
    tokenContents,
    isGCADmin,
    isProjectArchived,
    userAction,
    currentUser,
    setInviteAccUser,
    projectCompanies: projectCompanies?.project_vendors || []
  };

  const isPermissionEditable = useCallback((params: any) => {
    const { data, context } = params;

    const editEnabledBasedOnInviteStatus =
      params?.data?.status_id === AcceptanceStatus.INVITED ||
      params?.data?.status_id === AcceptanceStatus.ACTIVE;

    if (!editEnabledBasedOnInviteStatus) return false;

    const isGcADmin = isGcProjectAdminRole(context?.tokenContents?.role);

    const rolePermission = !isPermissionNotGrantted(
      ProjectPermissionEnum.AddProjectUser,
      context?.tokenContents?.role ?? ""
    );

    if (!rolePermission) return false;

    if (isGcADmin) return !data.external;
    return false;
  }, []);

  const cellStyleForDeactivatedUsers = (params: any) => {
    if (params.data.status_id === AcceptanceStatus.DEACTIVATED) {
      return { opacity: 0.5 };
    }
    return { opacity: 1.0 };
  };

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (eventLogs.some((log) => log.data_source === "project_vendors")) {
        refetchProjectCompanies();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [eventLogs, refetchProjectCompanies]);

  // Each Column Definition results in one Column.
  const columnDefs = useMemo<ColDef[]>(() => {
    const colArr: ColDef[] = [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        width: 100,
        sortable: false,
        menuTabs: [],
        editable: false,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        valueGetter: (params: any) => {
          return `${params.data.user.first_name} ${params.data.user.last_name}`;
        },
        field: "name",
        colId: "name",
        headerName: "Name",
        headerTooltip: "Name",
        sort: "asc",
        minWidth: 120,
        cellStyle: cellStyleForDeactivatedUsers,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "subscription_vendor.name",
        colId: "company",
        headerName: "Company Name",
        headerTooltip: "Company Name",
        filter: true,
        cellStyle: cellStyleForDeactivatedUsers,
        editable: (params) => {
          return hasProjectUserCompanyEditAccess(params);
        },
        cellRenderer: userCompanyRenderer,
        cellEditor: UserCompanySelectCellEditor,
        cellClass: (params: any) => {
          return hasProjectUserCompanyEditAccess(params) ? "cell-editable" : "";
        }
      },
      {
        headerName: "Email",
        headerTooltip: "Email",
        field: "user.email",
        colId: "user.email",
        cellStyle: cellStyleForDeactivatedUsers,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "user.phone",
        colId: "user.phone",
        headerName: "Phone Number",
        headerTooltip: "Phone Number",
        cellStyle: cellStyleForDeactivatedUsers,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "project_role.name",
        headerName: "Project Role",
        headerTooltip: "Project Role",
        filter: true,
        editable: (params) => {
          return isPermissionEditable(params);
        },
        cellRenderer: (params: any) => {
          return projectUserRoleRenderer(params);
        },
        cellEditor: ProjectUserRoleSelectCellEditor,
        cellClass: (params: any) => {
          return isPermissionEditable(params) ? "cell-editable" : "";
        },
        minWidth: 240,
        cellStyle: cellStyleForDeactivatedUsers,
        valueGetter: ({ data }) => {
          return data?.project_role?.name || "";
        }
      },
      {
        colId: "source",
        headerName: "Source",
        hide: !isIntegrationMode,
        valueGetter: ({ data }) => {
          const sources = getProjectUserSources(data);
          return sources.join(",");
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        field: "status_id",
        colId: "status_id",
        headerName: "User Status",
        headerTooltip: "User Status",
        minWidth: 300,
        cellRenderer: InviteStatusCellRenderer,
        cellRendererParams: { inviteType: "ProjectUser" },
        comparator: (valueA: any, valueB: any) => {
          return AcceptanceStatus[valueA]
            .toLowerCase()
            .localeCompare(AcceptanceStatus[valueB].toLowerCase());
        },
        tooltipValueGetter: (params) => {
          let status = "";
          switch (params.data.status_id) {
            case 1:
              status = "Pending";
              break;
            case 2:
              status = "Active";
              break;
            case 3:
              status = "Inactive";
              break;
            case 4:
              status = "Not Invited";
              break;

            default:
              break;
          }
          return status;
        },
        cellStyle: cellStyleForDeactivatedUsers
      }
    ];

    return colArr;
  }, [isIntegrationMode, isPermissionEditable]);

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      resizable: true,
      comparator: comparatorWithEmptyAtBottom
    };
  }, []);

  const setDrawerAction = (drawerAction: boolean) => {
    setDrawerVisibility(drawerAction);
  };

  const projectUsersData = useMemo(() => {
    const subscriptionUsers: any[] = internalUsers?.project_users?.filter(
      (user: any) => {
        return subscriptionId === user?.subscription_id;
      }
    );

    const isSCUser = matchCustomUserProjectRole(
      ECustomUserTypes.AllSCRoles,
      tokenContents?.role
    );

    if (isSCUser) {
      const filteredScUsers: any[] = subscriptionUsers?.filter((user: any) => {
        return (
          !user?.subscription_vendor ||
          tokenContents?.claims["x-hasura-vendor-id"] ===
            user?.subscription_vendor?.id
        );
      });
      return filteredScUsers || [];
    }
    return subscriptionUsers;
  }, [internalUsers, subscriptionId, tokenContents]);

  useEffect(() => {
    function handleResize() {
      if (isGridReady && gridRef && gridRef.current) {
        gridRef?.current?.api?.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
  }, [isGridReady]);

  const onUserPermissionCellEdit = async (event: any) => {
    if (event.newValue === event.oldValue) return;
    const userRole = allProjectRoles?.project_role?.find(
      (role: any) => role?.id === event.newValue
    );
    if (userRole?.id) {
      const oldData = { ...event.data };
      event.node.setData({
        ...oldData,
        project_role: userRole
      });

      const variables = {
        role_id: userRole?.id,
        user_id: event.data?.user_id
      };

      try {
        const updateResponse = await updateUserPermission({
          variables
        });
        if (updateResponse.data?.update_users_project_role?.message) {
          message.success(
            updateResponse.data?.update_users_project_role?.message
          );
        } else {
          message.error("Failed to update.");
          event.node.setData(oldData);
        }
      } catch (ex) {
        console.error(ex);
        message.error("An error occured");
        event.node.setData(oldData);
      }
    }
  };

  const cellEditRequest = async (event: any) => {
    if (!event.newValue) return;
    if (event.column.colId === "project_role.name") {
      onUserPermissionCellEdit(event);
      return;
    }

    if (event.column.colId === "company") {
      onCompanyCellEdit(event);
    }
  };

  const onFiltersApplied = () => {
    filterChipRef.current?.onFilterApplied();
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const headerItems = [
    <CiqAgSearch
      key="ProjectUser_Search"
      gridRef={gridRef}
      placeholder="Search Users"
    />,
    <FilterChipComponent
      key="ProjectUser_Filter"
      gridRef={gridRef}
      ref={filterChipRef}
    />,
    <Button
      key="ProjectUser_Permissions"
      onClick={() => {
        history.replace(
          `/project/${projectId}/settings/general/project-users/permissions`
        );
      }}
    >
      View Permission Matrix
    </Button>,
    <Button
      key="ProjectUser_NewAdd"
      type="primary"
      onClick={() => {
        setDrawerVisibility(true);
      }}
      title={
        isProjectArchived
          ? undefined
          : isPermissionNotGrantted(
                ProjectPermissionEnum.AddProjectUser,
                tokenContents?.role ?? ""
              )
            ? ErrorMessages.PermissionNotGranted
            : "Invite User"
      }
      disabled={isPermissionNotGrantted(
        ProjectPermissionEnum.AddProjectUser,
        tokenContents?.role ?? ""
      )}
      data-testid="invite-project-user-button"
    >
      Invite User
    </Button>
  ];

  return (
    <div
      className="flex flex-col"
      style={{ height: `calc(100vh - 140px - ${ARCHIVED_BANNER_HEIGHT})` }}
    >
      <CiqLogPageHeader
        {...{
          entityName: "Users",
          titleParam: {
            title: "Users",
            totalCount: projectUsersData?.length,
            filterStats: statsData
          },
          gridRef,
          items: headerItems
        }}
      />
      <div className="grow ag-theme-alpine">
        <AgGridReact<any>
          ref={gridRef}
          onGridReady={() => {
            setGridReady(true);
            gridRef?.current?.api?.sizeColumnsToFit();
          }}
          rowData={projectUsersData}
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={false} // Optional - set to 'true' to have rows animate when sorted
          loadingOverlayComponent={GridLoadingIndicator}
          suppressDragLeaveHidesColumns
          readOnlyEdit
          singleClickEdit
          onCellEditRequest={cellEditRequest}
          context={gridContext}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          tooltipShowMode="whenTruncated"
          stopEditingWhenCellsLoseFocus
          onFilterChanged={onFiltersApplied}
          suppressScrollOnNewData
        />
      </div>
      {drawer && (
        <ErrorBoundary
          onError={() => {
            setDrawerVisibility(false);
          }}
        >
          <InviteProjectUser
            existingUsers={projectUsersData}
            setDrawerOpen={setDrawerAction}
            vendorData={vendorData}
            showDrawerOpen={drawer}
            modelTitle="Invite User"
          />
        </ErrorBoundary>
      )}

      {inviteAccUser.showInviteModel && (
        <ErrorBoundary
          onError={() => {
            setInviteAccUser({
              showInviteModel: false,
              inviteUserData: null
            });
          }}
        >
          <InviteAccUserComponent
            setDrawerOpen={setInviteAccUser}
            showDrawerOpen={inviteAccUser.showInviteModel}
            modelTitle={DialogTitles.inviteUser}
            userData={inviteAccUser.inviteUserData}
            isFromProjectUser
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

export default ProjectUsersList;
